import RoundAlertIcon from 'assets/svg/alert/round-alert-icon.svg'
import CancelIcon from 'assets/svg/cancel/cancel-icon.svg'
import Button from 'common/components/Button'
import Flex from 'common/components/Flex'
import Ticker from 'common/components/Ticker'
import Typography from 'common/components/Typography'
import theme from 'common/theme'
import { setCookie } from 'lib/cookie'
import expiration from 'lib/expiry'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import filterSmartAlert from 'services/Home/strategies/filter-smart-alert'
import {
  ISmartAlert,
  ISmartAlertMarketplaceDocument,
} from 'services/Home/widgets/SmartAlert/interface'
import smartAlertMarketplaceDocument from 'services/Home/widgets/SmartAlert/smart-alert-marketplace-document'
import { useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const Container = styled.div`
  margin: 16px auto;
  width: ${theme.layout.containerWidth};
`

const LeftSide = styled.div`
  display: flex;
  gap: 13px;
`

const RightSide = styled.div`
  display: flex;
  flex-shrink: 0;
`

const OrangeAlertIcon = styled(RoundAlertIcon)`
  color: ${theme.colors.orange};
  flex-shrink: 0;
`

const StyledCancelIcon = styled(CancelIcon)`
  transform: scale(1.6);
  color: ${theme.colors.grayDark};
  :hover {
    transition: 0.3s;
    transform: scale(1.25);
    cursor: pointer;
  }
`

const WrapperButtonRedirect = styled.a`
  margin: auto 16px;
`

const StyledFlex = styled(Flex)`
  padding: 16px;
  ${({ numberOfActiveAlerts }) =>
    numberOfActiveAlerts > 1 && 'padding-bottom: 32px;'};
  box-sizing: border-box;
`

const StyledLink = styled.a`
  color: ${theme.colors.orange};
  text-decoration: underline;
  font-weight: bold;
  cursor: pointer;
`

function SmartAlert({ page }: ISmartAlert): JSX.Element {
  const [activeAlerts, setActiveAlerts] = useState([])
  const { firstStore } = useStoreState((states) => states.storeSeller)

  const closeAlert = useCallback(
    (e) => {
      const alertKey = e.currentTarget.getAttribute('data-alert-key')
      const visibility = e.currentTarget.getAttribute(
        'data-visibility',
      )
      const cookieName = `${page}-${alertKey}`

      if (visibility === 'once_per_day') {
        const tomorrow = expiration(24 * 60 * 60)
        setCookie(
          cookieName,
          tomorrow.getTime().toString(),
          undefined,
          tomorrow,
        )
      }
      if (visibility === 'once_per_week') {
        const nextWeek = expiration(7 * 24 * 60 * 60)
        setCookie(
          cookieName,
          nextWeek.getTime().toString(),
          undefined,
          nextWeek,
        )
      }
      if (visibility === 'once_per_month') {
        const nextMonth = expiration(30 * 24 * 60 * 60)
        setCookie(
          cookieName,
          nextMonth.getTime().toString(),
          undefined,
          nextMonth,
        )
      }

      setActiveAlerts(
        activeAlerts.filter((alert) => alert.alert_key !== alertKey),
      )
    },
    [activeAlerts, page],
  )

  useEffect(() => {
    const data: ISmartAlertMarketplaceDocument[] =
      smartAlertMarketplaceDocument?.data?.[page]

    if (data) {
      const sortedAlerts = [...data].sort(
        (a, b) => a.order_key - b.order_key,
      )
      const filteredAlerts = sortedAlerts.filter((alert) =>
        filterSmartAlert(alert, page),
      )
      setActiveAlerts(filteredAlerts)
    }
  }, [page, firstStore])

  const slides = activeAlerts.map((alert) => {
    return (
      <StyledFlex
        numberOfActiveAlerts={activeAlerts.length}
        justifyContent="space-between"
        key={alert.alert_id}
      >
        <LeftSide>
          <OrangeAlertIcon />
          <Flex flexDirection="column">
            <Typography type="bsubtitle" marginBottom={10}>
              {alert.title}
            </Typography>
            <Typography data-testid="smart-alert-message">
              {alert.alert_key === 'scam-fraud-alert' ? (
                <>
                  Pastikan transaksi hanya di
                  <span> Ralali.com </span>
                  atau hubungi WA
                  <span> 0818-500-465. </span>
                  Cek info lengkap
                  {` `}
                  <Link href="">
                    <StyledLink
                      href="https://news.ralali.com/waspada-penipuan-mengatasnamakan-ralali/"
                      target="__blank"
                    >
                      Klik Disini
                    </StyledLink>
                  </Link>
                </>
              ) : (
                alert.content
              )}
            </Typography>
          </Flex>
        </LeftSide>
        <RightSide>
          {alert.redirect?.is_active ? (
            <WrapperButtonRedirect href={alert.redirect.url}>
              <Button
                variant="outlined"
                fontWeight="bold"
                borderRadius="10px"
                transparent
              >
                {alert.redirect.title}
              </Button>
            </WrapperButtonRedirect>
          ) : null}
          <StyledCancelIcon
            data-alert-key={alert.alert_key}
            data-visibility={alert.configuration.visibility}
            onClick={closeAlert}
            data-testid="smart-alert-close-button"
          />
        </RightSide>
      </StyledFlex>
    )
  })

  if (!activeAlerts || !activeAlerts.length) return null

  return (
    <Container data-testid="smart-alert-container">
      <Ticker slides={slides} />
    </Container>
  )
}

export default SmartAlert
